import DataTable, { Row } from "components/DataTable";
import GenericModal from "components/Modal";
import React, { useContext, useEffect, useState } from "react";
import apiService from "services/apiService";
import AddTaxConfig from "./AddTaxConfig";
import AddTaxSetup from "./AddTaxSetup";
import UpdateTaxConfig from "./UpdateTaxConfig";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import DeletePopup from "components/DeletePopup/deletePopup";
import { useToaster } from "contexts/toasterContext";
import { BannerContext } from "components/SideBar";

interface TaxProps {
  id: number;
  financialYear: number;
  slabStart: number;
  taxRate: number;
  cessRate: number;
  standardDeduction: number;
  rebateLimit: number;
  rebateIncomeThreshold: number;
}

const TaxConfig = () => {
  const { addToast } = useToaster();
  const [isAdding, setIsAdding] = useState(false);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [taxData, setTaxData] = useState<Row[]>([]);
  const [isUpdating, setisUpdating] = useState(false);
  const [updateData, setupdateData] = useState<TaxProps>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteData, setDeleteData] = useState<TaxProps>();
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [, setUserId] = useState<string | null>(null);

  const year = new Date().getFullYear();

  const getTaxConfigaration = () => {
    try {
      setIsLoading(true);
      apiService
        .get(`api/tax/${year}`)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((response: any) => {
          // Fix: Safely check if data exists and is an array.
          // If backend returns empty/null after last delete, it won't crash on .map()
          if (response?.data && Array.isArray(response.data)) {
            setTaxData(
              response.data.map((item: TaxProps) => ({
                ...item,
              })),
            );
          } else {
            setTaxData([]); // Explicitly clear if no data is found
          }
        })
        .catch((error) => {
          console.log(error);
          // Fix: If backend throws 404/error when the list is empty, clear the state!
          setTaxData([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      console.log(error);
      setTaxData([]);
    }
  };

  useEffect(() => {
    const loggedInUserId = localStorage.getItem("userId");
    setUserId(loggedInUserId);
    getTaxConfigaration();
  }, [refresh]);

  const { isBannerVisible } = useContext(BannerContext);
  const dynamicHeightClass = isBannerVisible
    ? "h-[calc(100vh-112px)] min-h-[300px]"
    : "h-[calc(100vh-80px)] min-h-[300px]";

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleUpdate = (row: any) => {
    setisUpdating(true);
    setupdateData(row);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDelete = (row: any) => {
    setIsDeleting(true);
    setDeleteData(row);
  };

  const deleteRow = (id: number) => {
    apiService
      .delete(`api/tax/${id}`)
      .then(() => {
        addToast("Tax Config deleted successfully", "success");

        // Fix: Optimistically update the UI for immediate removal, 
        // passing down the exact new state to DataTable.
        setTaxData((prevData) => prevData.filter((item) => item.id !== id));

        // Continue with standard refresh to sync with backend
        setRefresh((prev) => prev + 1);
      })
      .catch((error) => {
        console.log(error);
        addToast(
          error?.response?.data?.message || "Failed to delete Tax Config",
          "error",
        );
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const onClose = () => {
    setIsAdding(false);
  };

  const dataColumn = [
    { key: "financialYear", label: "Year", bold: true },
    { key: "slabStart", label: "Slab Start", bold: true },
    { key: "taxRate", label: "Tax Rate", bold: true },
    { key: "cessRate", label: "Cess Rate ", bold: true },
    { key: "standardDeduction", label: "Standard Deduction", bold: true },
    { key: "rebateLimit", label: "Rebate Limit", bold: true },
    { key: "rebateIncomeThreshold", label: "Rebate Income Threshold", bold: true },
    {
      key: "actions",
      label: "Actions",
      excludeExport: true,
      actions: (row: object) => (
        <div className="flex flex-start">
          <span
            onClick={() => handleUpdate(row)}
            className="btn-primary px-2 py-2 mr-2 rounded-lg cursor-pointer"
            title="Update"
          >
            <FaEdit />
          </span>
          <span
            onClick={() => handleDelete(row)}
            className="btn-red px-2 py-2 rounded-lg cursor-pointer"
            title="Delete"
          >
            <MdDelete className="text-white" />
          </span>
        </div>
      ),
    },
  ];

  return (
    <div>
      <DataTable
        heightClass={dynamicHeightClass}
        data={taxData}
        columns={dataColumn}
        loading={isLoading}
        preAddButtons={
          <button
            onClick={() => setIsSettingUp(true)}
            className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 shadow-lg active:scale-95 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center me-2 transition-all"
          >
            Tax Config
          </button>
        }
      ></DataTable>
      {isAdding && (
        <GenericModal isOpen={isAdding} onClose={onClose} header="Add Tax Config">
          <div className="bg-white dark:bg-gray-800">
            <AddTaxConfig onClose={onClose} refresh={refresh} setRefresh={setRefresh} />
          </div>
        </GenericModal>
      )}
      {isSettingUp && (
        <GenericModal
          isOpen={isSettingUp}
          onClose={() => setIsSettingUp(false)}
          header="Tax Config"
          size="lg"
        >
          <div className="bg-white dark:bg-gray-800">
            <AddTaxSetup
              onClose={() => setIsSettingUp(false)}
              refresh={refresh}
              setRefresh={setRefresh}
            />
          </div>
        </GenericModal>
      )}
      {isUpdating && (
        <GenericModal
          isOpen={isUpdating}
          onClose={() => setisUpdating(false)}
          header="Update Tax Config"
          size="lg"
        >
          <div className="bg-white dark:bg-gray-800">
            <UpdateTaxConfig
              updateData={updateData}
              onClose={() => setisUpdating(false)}
              refresh={refresh}
              setRefresh={setRefresh}
            />
          </div>
        </GenericModal>
      )}
      {isDeleting && (
        <GenericModal
          isOpen={isDeleting}
          onClose={() => setIsDeleting(false)}
          header="Delete Tax Config"
        >
          <DeletePopup
            handleDelete={deleteRow}
            onClose={() => setIsDeleting(false)}
            selectedRow={deleteData}
            message="Are you sure you want to delete this Tax Config?"
          />
        </GenericModal>
      )}
    </div>
  );
};
export default TaxConfig;